import { useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseQueryResult,
  type UseQueryOptions,
  type UseMutationResult,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useErrorBus } from '@/errors/useErrorBus';
import { normalizeError } from '@/errors/normalize';
import { useToken } from '@/hooks/useToken';
import type { NormalizedApiError } from '@/api/types';

const DEFAULT_QUERY_OPTIONS = {
  retry: (failureCount: number, error: NormalizedApiError) => {
    if (error?.category === 'validation' || error?.category === 'auth') {
      return false;
    }
    return failureCount < 2;
  },
};

// Query options minus the fields useApiQuery manages itself, plus our extras.
export type ApiQueryOptions<TData> = Partial<
  UseQueryOptions<TData, NormalizedApiError>
> & {
  background?: boolean;
};

export type ApiMutationOptions<TData, TVariables> = Partial<
  UseMutationOptions<TData, NormalizedApiError, TVariables>
> & {
  publishErrors?: boolean;
};

/**
 * Enhanced useApiQuery - React Query wrapper for API calls
 * Uses centralized API modules for consistent data fetching
 */
export function useApiQuery<TData>(
  key: QueryKey,
  apiCall: () => Promise<AxiosResponse<TData>>,
  options: ApiQueryOptions<TData> = {},
): UseQueryResult<TData, NormalizedApiError> {
  const { publish } = useErrorBus();
  const { background = false, ...queryOptions } = options;

  const queryFn = useCallback(async () => {
    try {
      // Call the centralized API function
      const response = await apiCall();
      return response.data;
    } catch (error) {
      if (background) {
        publish(error);
      }
      throw error;
    }
  }, [apiCall, background, publish]);

  return useQuery<TData, NormalizedApiError>({
    queryKey: key,
    queryFn,
    ...DEFAULT_QUERY_OPTIONS,
    ...queryOptions,
  });
}

/**
 * Enhanced useApiMutation - React Query wrapper for mutations
 * Uses centralized API modules for consistent data mutations
 */
export function useApiMutation<TData, TVariables>(
  apiCallFactory: (variables: TVariables) => Promise<AxiosResponse<TData>>,
  options: ApiMutationOptions<TData, TVariables> = {},
): UseMutationResult<TData, NormalizedApiError, TVariables> {
  const { publish } = useErrorBus();
  const { publishErrors = true, ...mutationOptions } = options;

  return useMutation<TData, NormalizedApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      try {
        // Call the centralized API function with variables
        const response = await apiCallFactory(variables);
        return response.data;
      } catch (error) {
        if (publishErrors) {
          publish(error);
        }
        throw error;
      }
    },
    // A caller-supplied onError (via ...mutationOptions) flows straight through.
    ...mutationOptions,
  });
}

/**
 * Hook for inline error handling
 */
export function useInlineError(error: unknown): NormalizedApiError | null {
  if (!error) return null;
  return normalizeError(error);
}

/**
 * Convenience hooks for authenticated API calls.
 * The auth token is resolved from the user store — callers never pass it.
 */
export function useAuthenticatedQuery<TData>(
  key: QueryKey,
  apiCallFactory: (token: string) => Promise<AxiosResponse<TData>>,
  options: ApiQueryOptions<TData> = {},
): UseQueryResult<TData, NormalizedApiError> {
  const token = useToken();

  const apiCall = useCallback(() => {
    if (!token) {
      throw new Error('No authentication token available');
    }
    return apiCallFactory(token);
  }, [apiCallFactory, token]);

  const { enabled = true, ...queryOptions } = options;

  return useApiQuery<TData>(key, apiCall, {
    ...queryOptions,
    // Never run without a token, honor the caller's own gating on top
    enabled: !!token && enabled !== false,
  });
}

export function useAuthenticatedMutation<TData, TVariables = void>(
  apiCallFactory: (
    variables: TVariables & { token?: string },
  ) => Promise<AxiosResponse<TData>>,
  options: ApiMutationOptions<TData, TVariables> = {},
): UseMutationResult<TData, NormalizedApiError, TVariables> {
  const token = useToken();

  return useApiMutation<TData, TVariables>(
    variables => apiCallFactory({ ...variables, token }),
    options,
  );
}

/**
 * Optimistic updates hook
 */
export function useOptimisticMutation<TData, TVariables, TContext = unknown>(
  apiCallFactory: (variables: TVariables) => Promise<TData>,
  queryKey: QueryKey,
  optimisticUpdate?: (previousData: unknown, variables: TVariables) => unknown,
  options: ApiMutationOptions<TData, TVariables> = {},
): UseMutationResult<TData, NormalizedApiError, TVariables> {
  const { publish } = useErrorBus();
  const queryClient = useQueryClient();
  const { publishErrors = true, ...mutationOptions } = options;

  return useMutation<TData, NormalizedApiError, TVariables>({
    mutationFn: apiCallFactory,
    onMutate: async (variables: TVariables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update
      if (optimisticUpdate) {
        queryClient.setQueryData(
          queryKey,
          optimisticUpdate(previousData, variables),
        );
      }

      return { previousData } as TContext;
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      const previous = (context as { previousData?: unknown })?.previousData;
      if (previous !== undefined) {
        queryClient.setQueryData(queryKey, previous);
      }

      if (publishErrors) {
        publish(_error);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey });
    },
    // A caller-supplied onError (via ...mutationOptions) overrides the default
    // rollback handler above, matching the prior behavior.
    ...mutationOptions,
  });
}
